import { NETWORK_PROTOCOL_VERSION } from './networkSimulation.js';
import { isGameVersionAtLeast, normalizeGameVersion } from './versioning.js';
import {
  applyMatchSnapshotDelta,
  decodeSnapshotPacket,
  encodeMatchInput,
  isBinarySnapshotPacket,
} from '../../shared/binarySnapshot.js';

const DEFAULT_SERVER_URL=import.meta.env?.VITE_GAME_SERVER_URL||'ws://127.0.0.1:8080';
const CLIENT_VERSION=normalizeGameVersion(
  typeof __APP_VERSION__==='string'?__APP_VERSION__:'0.0.0',
)||'0.0.0';
const RECOVERY_STORAGE_KEY='toybox-online-recovery-v1';
const DEFAULT_RECONNECT_GRACE_MS=15_000;
const NETWORK_PROBE_INTERVAL_MS=2000;
const NETWORK_PROBE_TIMEOUT_MS=6000;

function normalizeServerUrl(value){
  let raw=String(value||DEFAULT_SERVER_URL).trim();
  if(!/^[a-z]+:\/\//i.test(raw))raw=`ws://${raw}`;
  const url=new URL(raw);
  if(url.protocol==='http:')url.protocol='ws:';
  if(url.protocol==='https:')url.protocol='wss:';
  if(url.protocol!=='ws:'&&url.protocol!=='wss:')throw new Error('La dirección debe usar ws:// o wss://.');
  return url.toString().replace(/\/$/,'');
}

function connectionUrl(serverUrl){
  const url=new URL(serverUrl);
  url.searchParams.set('clientVersion',CLIENT_VERSION);
  url.searchParams.set('protocolVersion',String(NETWORK_PROTOCOL_VERSION));
  return url.toString();
}

function updateRequiredError(message={}){
  const minimum=normalizeGameVersion(message.minimumClientVersion);
  const error=new Error(minimum
    ?`Esta versión ya no puede jugar online. Actualizá The Turbo Crown a la versión ${minimum} o posterior desde Steam.`
    :'Esta versión ya no puede jugar online. Actualizá The Turbo Crown desde Steam.');
  error.code='UPDATE_REQUIRED';
  error.clientVersion=CLIENT_VERSION;
  error.minimumClientVersion=minimum;
  return error;
}

export function remainingMatchStartMs(match,serverNow=Date.now()){
  const startsAt=Number(match?.startsAt);
  if(Number.isFinite(startsAt))return Math.max(0,startsAt-serverNow);
  return Math.max(0,Number(match?.startsInMs)||0);
}

function readRecovery(){
  try{return JSON.parse(sessionStorage.getItem(RECOVERY_STORAGE_KEY)||'null');}
  catch{return null;}
}

function decodeNetworkMessage(raw){
  if(raw&&typeof raw==='object'&&typeof raw.type==='string')return raw;
  try{
    if(typeof raw==='string')return JSON.parse(raw);
  }catch{}
  return null;
}

export class OnlineClient {
  constructor(callbacks={}){
    this.callbacks=callbacks;
    this.socket=null;
    this.status='offline';
    this.playerId=null;
    this.room=null;
    this.match=null;
    try{this.serverUrl=normalizeServerUrl(localStorage.getItem('toybox-server-url')||DEFAULT_SERVER_URL);}
    catch{this.serverUrl=DEFAULT_SERVER_URL;}
    const recovery=readRecovery();
    this.playerId=recovery?.url===this.serverUrl?recovery.playerId:null;
    this.roomCode=recovery?.url===this.serverUrl?recovery.roomCode:null;
    this.resumeToken=recovery?.url===this.serverUrl?recovery.resumeToken:null;
    this.reconnectGraceMs=recovery?.reconnectGraceMs||DEFAULT_RECONNECT_GRACE_MS;
    this.reconnectTimer=null;
    this.resumeTimer=null;
    this.reconnectDeadline=0;
    this.reconnectAttempts=0;
    this.intentionalSockets=new WeakSet();
    this.pingTimer=null;
    this.pingSequence=0;
    this.pendingPings=new Map();
    this.rttSamples=[];
    this.probeOutcomes=[];
    this.snapshotOutcomes=[];
    this.lastSnapshotTick=null;
    this.lastSnapshotMatchId=null;
    this.snapshotBase=null;
    this.snapshotResyncPending=false;
    this.lastNetworkEmitAt=0;
    this.networkStats={rtt:null,jitter:0,loss:0,quality:'offline'};
    this.serverClockOffsetMs=null;
  }

  setStatus(status,detail=''){
    this.status=status;
    this.callbacks.onStatus?.({status,detail,url:this.serverUrl});
    this.emitNetworkStats(true);
  }

  async connect(url=this.serverUrl){
    let normalized;
    try{normalized=normalizeServerUrl(url);}
    catch(error){this.setStatus('error',error.message);throw error;}
    if(this.socket?.readyState===WebSocket.OPEN&&normalized===this.serverUrl)return true;
    this.cancelReconnect();
    if(normalized!==this.serverUrl){
      if(this.room)this.send({type:'room:leave'},true);
      this.clearSession(true);
    }
    const previousSocket=this.socket;
    this.socket=null;
    if(previousSocket&&previousSocket.readyState<2){
      this.intentionalSockets.add(previousSocket);
      previousSocket.close(1000,'Nueva conexión');
    }
    this.serverUrl=normalized;
    localStorage.setItem('toybox-server-url',normalized);
    const recovering=this.hasRecovery();
    this.setStatus(recovering?'reconnecting':'connecting',recovering?'Recuperando la sesión anterior…':'');
    return this.openSocket(recovering);
  }

  openSocket(recovering=false){
    return new Promise((resolve,reject)=>{
      let settled=false;
      let opened=false;
      const socket=new WebSocket(connectionUrl(this.serverUrl));
      socket.binaryType='arraybuffer';
      this.socket=socket;
      const timeout=setTimeout(()=>{
        if(settled)return;
        settled=true;
        if(!recovering)this.intentionalSockets.add(socket);
        socket.close();
        if(!recovering)this.setStatus('error','El servidor tardó demasiado en responder.');
        reject(new Error('Tiempo de conexión agotado.'));
      },5000);
      socket.addEventListener('open',()=>{
        opened=true;
        this.setStatus(recovering?'reconnecting':'connecting',recovering
          ?'Validando versión y sesión reservada…'
          :'Validando versión del juego…');
      });
      socket.addEventListener('message',(event)=>{
        const message=decodeNetworkMessage(event.data);
        if(!settled&&message?.type==='server:hello'){
          const compatibleProtocol=message.protocolVersion===NETWORK_PROTOCOL_VERSION;
          const compatibleVersion=message.clientCompatible!==false
            &&isGameVersionAtLeast(CLIENT_VERSION,message.minimumClientVersion||CLIENT_VERSION);
          if(!compatibleProtocol||!compatibleVersion){
            settled=true;
            clearTimeout(timeout);
            const error=updateRequiredError(message);
            this.cancelReconnect();
            this.intentionalSockets.add(socket);
            this.setStatus('error',error.message);
            this.callbacks.onError?.(error);
            socket.close(4003,'Update required');
            reject(error);
            return;
          }
          settled=true;
          clearTimeout(timeout);
          this.startNetworkProbe();
          if(recovering){
            this.setStatus('reconnecting','Validando la sesión reservada…');
            this.send({
              type:'session:resume',
              roomCode:this.roomCode,
              playerId:this.playerId,
              resumeToken:this.resumeToken,
            },true);
            this.resumeTimer=setTimeout(()=>{
              if(this.socket===socket&&this.status==='reconnecting')socket.close(4000,'Reintento de sesión');
            },Math.min(5000,this.reconnectGraceMs));
          }else this.setStatus('online');
          resolve(true);
        }
        this.handleMessage(event.data);
      });
      socket.addEventListener('close',()=>{
        clearTimeout(timeout);
        if(!settled){
          settled=true;
          reject(new Error('El servidor cerró la conexión antes de validar la versión.'));
        }
        if(this.socket===socket){
          this.socket=null;
          this.stopNetworkProbe();
          if(this.intentionalSockets.has(socket))return;
          if(!opened&&!recovering&&this.status==='error')return;
          if(this.hasRecovery())this.scheduleReconnect();
          else{
            this.clearSession(true);
            this.setStatus('offline','Se cerró la conexión con el servidor.');
          }
        }
      });
      socket.addEventListener('error',()=>{
        if(!settled){
          settled=true;
          clearTimeout(timeout);
          if(!recovering)this.setStatus('error','No se pudo conectar con el servidor online.');
          reject(new Error('No se pudo conectar con el servidor.'));
        }
      });
    });
  }

  startNetworkProbe(){
    this.stopNetworkProbe(true);
    this.sendNetworkProbe();
    this.pingTimer=setInterval(()=>this.sendNetworkProbe(),NETWORK_PROBE_INTERVAL_MS);
  }

  stopNetworkProbe(reset=false){
    clearInterval(this.pingTimer);
    this.pingTimer=null;
    this.pendingPings.clear();
    if(reset){
      this.rttSamples.length=0;
      this.probeOutcomes.length=0;
      this.snapshotOutcomes.length=0;
      this.lastSnapshotTick=null;
      this.lastSnapshotMatchId=null;
      this.serverClockOffsetMs=null;
    }
    this.emitNetworkStats(true);
  }

  sendNetworkProbe(){
    const socket=this.socket;
    const now=performance.now();
    this.pendingPings.forEach((probe,id)=>{
      if(now-probe.performanceAt<NETWORK_PROBE_TIMEOUT_MS)return;
      this.pendingPings.delete(id);
      this.probeOutcomes.push(1);
    });
    this.probeOutcomes=this.probeOutcomes.slice(-20);
    if(socket?.readyState!==WebSocket.OPEN){this.emitNetworkStats(true);return;}
    const id=String(++this.pingSequence);
    const wallAt=Date.now();
    this.pendingPings.set(id,{performanceAt:now,wallAt});
    socket.send(JSON.stringify({type:'client:ping',id,clientSentAt:wallAt}));
    this.emitNetworkStats();
  }

  recordPong(message){
    const probe=this.pendingPings.get(String(message.id));
    if(probe===undefined)return;
    this.pendingPings.delete(String(message.id));
    const rtt=Math.max(0,performance.now()-probe.performanceAt);
    const serverTime=Number(message.serverTime);
    if(Number.isFinite(serverTime)){
      const measuredOffset=serverTime-(probe.wallAt+rtt*.5);
      this.serverClockOffsetMs=this.serverClockOffsetMs===null
        ?measuredOffset
        :this.serverClockOffsetMs+(measuredOffset-this.serverClockOffsetMs)*.2;
    }
    this.rttSamples.push(rtt);
    this.rttSamples=this.rttSamples.slice(-12);
    this.probeOutcomes.push(0);
    this.probeOutcomes=this.probeOutcomes.slice(-20);
    this.emitNetworkStats(true);
  }

  estimatedServerNow(){
    return Date.now()+(this.serverClockOffsetMs||0);
  }

  matchStartsInMs(match){
    return remainingMatchStartMs(match,this.estimatedServerNow());
  }

  recordSnapshot(message){
    if(this.lastSnapshotMatchId!==message.matchId){
      this.lastSnapshotMatchId=message.matchId;
      this.lastSnapshotTick=null;
      this.snapshotOutcomes.length=0;
    }
    const tick=Number(message.tick);
    if(!Number.isSafeInteger(tick))return;
    if(this.lastSnapshotTick!==null&&tick>this.lastSnapshotTick){
      const missed=Math.min(30,Math.max(0,tick-this.lastSnapshotTick-1));
      for(let index=0;index<missed;index+=1)this.snapshotOutcomes.push(1);
    }
    if(this.lastSnapshotTick===null||tick>this.lastSnapshotTick){
      this.snapshotOutcomes.push(0);
      this.lastSnapshotTick=tick;
    }
    this.snapshotOutcomes=this.snapshotOutcomes.slice(-120);
    this.emitNetworkStats();
  }

  emitNetworkStats(force=false){
    const now=performance.now();
    if(!force&&now-this.lastNetworkEmitAt<250)return;
    this.lastNetworkEmitAt=now;
    const recentRtt=this.rttSamples.slice(-5);
    const rtt=recentRtt.length?recentRtt.reduce((sum,value)=>sum+value,0)/recentRtt.length:null;
    const jitterSamples=this.rttSamples.slice(-8);
    let jitter=0;
    for(let index=1;index<jitterSamples.length;index+=1)jitter+=Math.abs(jitterSamples[index]-jitterSamples[index-1]);
    if(jitterSamples.length>1)jitter/=jitterSamples.length-1;
    const outcomes=this.snapshotOutcomes.length>=8?this.snapshotOutcomes:this.probeOutcomes;
    const loss=outcomes.length?outcomes.reduce((sum,value)=>sum+value,0)/outcomes.length*100:0;
    let quality=this.status==='online'?'checking':'offline';
    if(this.status==='online'&&rtt!==null){
      quality=loss>=12||rtt>=240?'poor':loss>=5||rtt>=150?'fair':rtt>=80?'good':'excellent';
    }
    this.networkStats={rtt:rtt===null?null:Math.round(rtt),jitter:Math.round(jitter),loss:Math.round(loss),quality};
    this.callbacks.onNetwork?.(this.networkStats);
  }

  hasRecovery(){
    return Boolean(this.playerId&&this.roomCode&&this.resumeToken);
  }

  persistRecovery(){
    if(!this.hasRecovery())return;
    try{
      sessionStorage.setItem(RECOVERY_STORAGE_KEY,JSON.stringify({
        url:this.serverUrl,
        playerId:this.playerId,
        roomCode:this.roomCode,
        resumeToken:this.resumeToken,
        reconnectGraceMs:this.reconnectGraceMs,
      }));
    }catch{}
  }

  cancelReconnect(){
    clearTimeout(this.reconnectTimer);
    clearTimeout(this.resumeTimer);
    this.reconnectTimer=null;
    this.resumeTimer=null;
  }

  scheduleReconnect(){
    if(this.reconnectTimer)return;
    if(!this.reconnectDeadline)this.reconnectDeadline=Date.now()+this.reconnectGraceMs;
    if(Date.now()>=this.reconnectDeadline){this.loseSession('No se pudo recuperar la sesión a tiempo.');return;}
    const delay=Math.min(3000,500*2**Math.min(3,this.reconnectAttempts++));
    this.setStatus('reconnecting',`Reconectando… intento ${this.reconnectAttempts}`);
    this.reconnectTimer=setTimeout(()=>{
      this.reconnectTimer=null;
      this.openSocket(true).catch(()=>{
        if(!this.socket)this.scheduleReconnect();
      });
    },delay);
  }

  loseSession(message){
    const hadSession=this.hasRecovery()||Boolean(this.room)||Boolean(this.match);
    this.cancelReconnect();
    this.clearSession(true);
    this.setStatus(this.socket?.readyState===WebSocket.OPEN?'online':'offline',message);
    if(hadSession)this.callbacks.onSessionLost?.({message});
  }

  handleMessage(raw){
    let message;
    if(isBinarySnapshotPacket(raw)){
      try{
        const packet=decodeSnapshotPacket(raw);
        if(packet.type==='match:snapshot-delta'){
          message=applyMatchSnapshotDelta(this.snapshotBase,packet);
        }else{
          message=packet;
        }
        this.snapshotBase=message;
        this.snapshotResyncPending=false;
      }catch{
        if(!this.snapshotResyncPending){
          this.snapshotResyncPending=true;
          this.send({type:'match:snapshot-resync',matchId:this.match?.id},true);
        }
        return;
      }
    }else message=decodeNetworkMessage(raw);
    if(!message)return;
    switch(message.type){
      case 'server:hello':
        break;
      case 'server:pong':
        this.recordPong(message);
        break;
      case 'session':
        this.playerId=message.playerId;
        this.roomCode=message.roomCode;
        this.resumeToken=message.resumeToken;
        this.reconnectGraceMs=message.reconnectGraceMs||DEFAULT_RECONNECT_GRACE_MS;
        this.reconnectDeadline=0;
        this.reconnectAttempts=0;
        this.cancelReconnect();
        this.persistRecovery();
        this.setStatus('online',message.resumed?'Sesión recuperada correctamente.':'');
        break;
      case 'room:state':
        this.room=message.room;
        if(this.room?.status==='lobby'){
          this.match=null;
          this.lastSnapshotMatchId=null;
          this.lastSnapshotTick=null;
          this.snapshotBase=null;
          this.snapshotResyncPending=false;
          this.snapshotOutcomes.length=0;
          this.emitNetworkStats(true);
        }
        this.callbacks.onRoom?.(this.room,this.playerId);
        break;
      case 'room:left':
        this.clearSession(true);
        break;
      case 'match:start':
        if(this.match?.id!==message.match.id){
          this.lastSnapshotMatchId=message.match.id;
          this.lastSnapshotTick=null;
          this.snapshotBase=null;
          this.snapshotResyncPending=false;
          this.snapshotOutcomes.length=0;
        }
        this.match=message.match;
        this.callbacks.onMatchStart?.(message.match,this.playerId);
        break;
      case 'match:snapshot':
        if(message.matchId===this.match?.id){
          this.recordSnapshot(message);
          this.callbacks.onSnapshot?.(message,this.playerId);
        }
        break;
      case 'error':
        if(message.code==='SESSION_EXPIRED'){
          this.callbacks.onError?.(message);
          this.loseSession(message.message);
          break;
        }
        this.callbacks.onError?.(message);
        break;
      default:break;
    }
  }

  send(message,silent=false){
    if(this.socket?.readyState!==WebSocket.OPEN){
      if(!silent)this.callbacks.onError?.({message:'No hay conexión con el servidor.'});
      return false;
    }
    this.socket.send(JSON.stringify(message));
    return true;
  }

  sendBinary(payload,silent=false){
    if(this.socket?.readyState!==WebSocket.OPEN){
      if(!silent)this.callbacks.onError?.({message:'No hay conexión con el servidor.'});
      return false;
    }
    this.socket.send(payload);
    return true;
  }

  createRoom({name,carId,loadout,collider,botCatalog,account,mode,mapId,botDifficulty}){
    return this.send({
      type:'room:create',
      player:{name,carId,loadout,collider,account},
      botCatalog,
      mode,
      mapId,
      botDifficulty,
    });
  }

  joinRoom({code,name,carId,loadout,collider,botCatalog,account}){
    return this.send({
      type:'room:join',
      code:String(code||'').toUpperCase(),
      player:{name,carId,loadout,collider,account},
      botCatalog,
    });
  }

  quickMatch({name,carId,loadout,collider,botCatalog,account,mode,mapId,botDifficulty}){
    return this.send({
      type:'matchmaking:quick',
      player:{name,carId,loadout,collider,account},
      botCatalog,
      mode,
      mapId,
      botDifficulty,
    });
  }

  configureRoom(mode,mapId,botDifficulty){
    return this.send({type:'room:configure',mode,mapId,botDifficulty});
  }

  setReady(ready,carId,loadout,collider){
    return this.send({type:'room:ready',ready,carId,loadout,collider});
  }

  startMatch(){
    return this.send({type:'room:start'});
  }

  prepareRematch(){
    return this.send({type:'room:rematch'});
  }

  sendMatchInput(matchId,seq,controls,metadata={}){
    return this.sendBinary(encodeMatchInput({
      type:'match:input',
      matchId,
      seq,
      controls,
      estimatedServerTime:Date.now()+(this.serverClockOffsetMs||0),
      clientRttMs:this.networkStats.rtt,
      remoteViewDelayMs:Number(metadata.remoteViewDelayMs)||0,
      contacts:Array.isArray(metadata.contacts)?metadata.contacts:[],
    }),true);
  }

  leaveRoom(){
    if(this.room)this.send({type:'room:leave'});
    this.clearSession(true);
  }

  disconnect(notify=true){
    if(notify&&this.room)this.send({type:'room:leave'});
    this.cancelReconnect();
    this.stopNetworkProbe(true);
    const socket=this.socket;
    this.socket=null;
    if(socket&&socket.readyState<2){
      this.intentionalSockets.add(socket);
      socket.close(1000,'Cliente desconectado');
    }
    if(notify)this.clearSession(true);
    if(notify)this.setStatus('offline');
  }

  clearSession(notify=false){
    this.room=null;
    this.match=null;
    this.playerId=null;
    this.roomCode=null;
    this.resumeToken=null;
    this.reconnectDeadline=0;
    this.reconnectAttempts=0;
    this.lastSnapshotMatchId=null;
    this.lastSnapshotTick=null;
    this.snapshotBase=null;
    this.snapshotResyncPending=false;
    this.snapshotOutcomes.length=0;
    try{sessionStorage.removeItem(RECOVERY_STORAGE_KEY);}catch{}
    if(notify)this.callbacks.onRoom?.(null,null);
  }
}
